// src/features/notifications/notifications.api.ts
// RTK Query API endpoints for Notification Center (Outbound broadcasts & In-App notifications)
import { baseApi } from '@/services/base.api'
import type {
  CreateBroadcastRequest,
  CreateBroadcastResponse,
  FetchBroadcastsParams,
  FetchBroadcastsResponse,
  NotificationSummaryMetrics,
  ApiResponse,
  FetchAppNotificationsResponse,
} from './notifications.types'
import { toast } from '@/utils/toast'

export const notificationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Outbound Broadcast Endpoints (Super Admin) ───────────────────────────
    
    // 1. Fetch Outbound Broadcast Notifications List
    getBroadcastNotifications: builder.query<FetchBroadcastsResponse, FetchBroadcastsParams>({
      query: (params) => ({
        url: '/notifications/super-admin',
        method: 'GET',
        params,
      }),
      transformResponse: (response: any): FetchBroadcastsResponse => {
        if (response?.content && Array.isArray(response.content)) {
          return {
            content: response.content,
            page: response.page || { size: response.content.length, number: 0, totalElements: response.content.length, totalPages: 1 },
          }
        }
        if (response?.data?.content && Array.isArray(response.data.content)) {
          return {
            content: response.data.content,
            page: response.data.page || { size: response.data.content.length, number: 0, totalElements: response.data.content.length, totalPages: 1 },
          }
        }
        if (response?.data?.notifications?.content && Array.isArray(response.data.notifications.content)) {
          return {
            content: response.data.notifications.content,
            page: response.data.notifications.page || { size: response.data.notifications.content.length, number: 0, totalElements: response.data.notifications.content.length, totalPages: 1 },
          }
        }
        return {
          content: [],
          page: { size: 10, number: 0, totalElements: 0, totalPages: 0 },
        }
      },
      providesTags: ['Notification'],
    }),

    // 2. Summary Metrics
    getNotificationSummary: builder.query<NotificationSummaryMetrics, void>({
      query: () => ({
        url: '/notifications/super-admin/summary',
        method: 'GET',
      }),
      transformResponse: (response: any): NotificationSummaryMetrics => {
        const raw = response?.data || response
        return {
          totalBroadcasts: raw?.totalBroadcasts ?? 0,
          unreadInboundAlertsCount: raw?.unreadInboundAlertsCount ?? raw?.unreadCount ?? 0,
          scheduledCount: raw?.scheduledCount ?? 0,
          deliverySuccessRatePct: raw?.deliverySuccessRatePct ?? 100,
        }
      },
      providesTags: ['Notification'],
    }),

    // 3. Unread Count for Super Admin Outbound / System
    getUnreadCount: builder.query<number, void>({
      query: () => ({
        url: '/notifications/super-admin/unread-count',
        method: 'GET',
      }),
      transformResponse: (response: any): number => {
        if (typeof response?.unreadCount === 'number') return response.unreadCount
        if (typeof response?.data?.unreadCount === 'number') return response.data.unreadCount
        return 0
      },
      providesTags: ['Notification'],
    }),

    // 4. Create & Broadcast Announcement
    createBroadcast: builder.mutation<CreateBroadcastResponse, CreateBroadcastRequest>({
      query: (body) => ({
        url: '/notifications/super-admin/broadcast',
        method: 'POST',
        data: body,
      }),
      invalidatesTags: ['Notification'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data?.message || 'Broadcast announcement sent successfully')
        } catch (err: any) {
          toast.error(err?.error?.data?.message || err?.data?.message || 'Failed to create broadcast')
        }
      },
    }),

    // 5. Mark Super Admin Notification as Read
    markNotificationAsRead: builder.mutation<ApiResponse<any>, string | number>({
      query: (id) => ({
        url: `/notifications/super-admin/${id}/read`,
        method: 'PUT',
      }),
      invalidatesTags: ['Notification'],
    }),

    // 6. Mark All Super Admin Notifications as Read
    markAllNotificationsAsRead: builder.mutation<ApiResponse<any>, void>({
      query: () => ({
        url: '/notifications/super-admin/read-all',
        method: 'PUT',
      }),
      invalidatesTags: ['Notification'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data?.message || 'All notifications marked as read')
        } catch {
          // Handled
        }
      },
    }),

    // 7. Cancel Super Admin Broadcast
    cancelBroadcast: builder.mutation<ApiResponse<any>, string | number>({
      query: (id) => ({
        url: `/notifications/super-admin/${id}/cancel`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data?.message || 'Notification broadcast cancelled successfully')
        } catch (err: any) {
          toast.error(err?.error?.data?.message || 'Failed to cancel broadcast')
        }
      },
    }),

    // ── In-App Notification Endpoints (`/api/v1/notifications/app-notification`) ──

    // 1. Unread In-App Notification Count
    getAppUnreadCount: builder.query<number, void>({
      query: () => ({
        url: '/notifications/app-notification/unread-count',
        method: 'GET',
      }),
      transformResponse: (response: any): number => {
        if (typeof response?.unreadCount === 'number') return response.unreadCount
        if (typeof response?.data?.unreadCount === 'number') return response.data.unreadCount
        return 0
      },
      providesTags: ['Notification'],
    }),

    // 2. Fetch In-App Notifications List
    getAppNotifications: builder.query<FetchAppNotificationsResponse, { page?: number; size?: number }>({
      query: (params) => ({
        url: '/notifications/app-notification',
        method: 'GET',
        params: { page: params?.page ?? 0, size: params?.size ?? 20 },
      }),
      transformResponse: (response: any): FetchAppNotificationsResponse => {
        const rawContent = response?.data?.content || response?.content || []
        const rawPage = response?.data?.page || response?.page || {
          size: rawContent.length,
          number: 0,
          totalElements: rawContent.length,
          totalPages: 1,
        }
        return {
          content: rawContent,
          page: rawPage,
        }
      },
      providesTags: ['Notification'],
    }),

    // 3. Mark In-App Notification as Read
    markAppNotificationAsRead: builder.mutation<ApiResponse<any>, string | number>({
      query: (id) => ({
        url: `/notifications/app-notification/${id}/read`,
        method: 'PUT',
      }),
      invalidatesTags: ['Notification'],
    }),

    // 4. Mark All In-App Notifications as Read
    markAllAppNotificationsAsRead: builder.mutation<ApiResponse<any>, void>({
      query: () => ({
        url: '/notifications/app-notification/read-all',
        method: 'PUT',
      }),
      invalidatesTags: ['Notification'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data?.message || 'All notifications marked as read')
        } catch {
          // Handled
        }
      },
    }),

    // 5. Delete In-App Notification
    deleteAppNotification: builder.mutation<ApiResponse<any>, string | number>({
      query: (id) => ({
        url: `/notifications/app-notification/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data?.message || 'Notification dismissed successfully')
        } catch (err: any) {
          toast.error(err?.error?.data?.message || 'Failed to dismiss notification')
        }
      },
    }),

    // 6. Delete/Clear All In-App Notifications
    clearAllAppNotifications: builder.mutation<ApiResponse<any>, void>({
      query: () => ({
        url: '/notifications/app-notification/clear-all',
        method: 'DELETE',
      }),
      invalidatesTags: ['Notification'],
      async onQueryStarted(_args, { queryFulfilled }) {
        try {
          const { data } = await queryFulfilled
          toast.success(data?.message || 'Notifications cleared successfully')
        } catch (err: any) {
          toast.error(err?.error?.data?.message || 'Failed to clear notifications')
        }
      },
    }),
  }),
})

export const {
  useGetBroadcastNotificationsQuery,
  useGetNotificationSummaryQuery,
  useGetUnreadCountQuery,
  useCreateBroadcastMutation,
  useMarkNotificationAsReadMutation,
  useMarkAllNotificationsAsReadMutation,
  useCancelBroadcastMutation,
  // In-App Notification Hooks
  useGetAppUnreadCountQuery,
  useGetAppNotificationsQuery,
  useMarkAppNotificationAsReadMutation,
  useMarkAllAppNotificationsAsReadMutation,
  useDeleteAppNotificationMutation,
  useClearAllAppNotificationsMutation,
} = notificationsApi
